import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MitgliedSuche from '@/components/MitgliedSuche';

const datum = (d) => (d ? d.split('-').reverse().join('.') : '');

// Empfehlungsaktion (30.09.2026): Zeitraum + Sonderpunkte, Empfehlung von Hand nachtragen
export default function EmpfehlungAktionKarte() {
  const [daten, setDaten] = useState(null);
  const [form, setForm] = useState(null);
  const [neu, setNeu] = useState(null);
  const [werber, setWerber] = useState(null);
  const [busy, setBusy] = useState(false);

  const laden = async () => {
    try {
      const r = await api.get('/empfehlung/aktion');
      setDaten(r.data);
      setForm(r.data.aktion || r.data.vorschlag);
    } catch { /* ohne Rechte: Karte bleibt leer */ }
  };
  useEffect(() => { laden(); }, []);
  if (!daten || !form) return null;
  const setzen = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const speichern = async (aktiv) => {
    setBusy(true);
    try {
      await api.put('/empfehlung/aktion', { titel: form.titel, von: form.von, bis: form.bis,
        punkte_werber: Number(form.punkte_werber), punkte_neu: Number(form.punkte_neu), aktiv });
      toast.success(aktiv ? 'Aktion gespeichert' : 'Aktion beendet'); laden();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };
  const nachtragen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/leads/${neu.id}/geworben-von`, { werber_id: werber.id });
      toast.success(r.data.gutgeschrieben ? `${r.data.werber}: Punkte gutgeschrieben` : `Gespeichert — Punkte kommen mit der Mitgliedschaft`);
      setNeu(null); setWerber(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Nachtragen fehlgeschlagen'); }
    setBusy(false);
  };
  const j = daten.jetzt;
  return (
    <Card data-testid="empfehlung-aktion">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Empfehlungsaktion</h2>
          <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${j.aktion ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
            {j.aktion ? `läuft: ${j.punkte_werber} / ${j.punkte_neu} Punkte` : `keine Aktion: ${j.punkte_werber} / ${j.punkte_neu} Punkte`}
          </span>
        </div>
        {!daten.aktion && <p className="text-xs text-muted-foreground">Vorschlag, noch nicht gespeichert:</p>}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <Input value={form.titel} onChange={e => setzen('titel', e.target.value)} className="h-9 col-span-2 md:col-span-1" aria-label="Titel" />
          <Input type="date" value={form.von} onChange={e => setzen('von', e.target.value)} className="h-9" aria-label="Von" />
          <Input type="date" value={form.bis} onChange={e => setzen('bis', e.target.value)} className="h-9" aria-label="Bis" />
          <label className="text-xs text-muted-foreground">Werber<Input type="number" min="0" value={form.punkte_werber} onChange={e => setzen('punkte_werber', e.target.value)} className="h-9" /></label>
          <label className="text-xs text-muted-foreground">Neumitglied<Input type="number" min="0" value={form.punkte_neu} onChange={e => setzen('punkte_neu', e.target.value)} className="h-9" /></label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => speichern(true)} disabled={busy}>Aktion speichern</Button>
          {daten.aktion?.aktiv && <Button size="sm" variant="outline" onClick={() => speichern(false)} disabled={busy}>Aktion beenden</Button>}
          <span className="text-xs text-muted-foreground">Gilt für Abschlüsse vom {datum(form.von)} bis {datum(form.bis)}. Außerhalb: {daten.standard?.punkte_werber ?? 500} / {daten.standard?.punkte_neu ?? 100} Punkte.</span>
        </div>
        <div className="border-t border-border/60 pt-3 space-y-2">
          <p className="text-sm font-medium">Empfehlung nachtragen (ohne Empfehlungslink geworben)</p>
          <div className="grid md:grid-cols-2 gap-2">
            <MitgliedSuche wert={neu} onWahl={setNeu} placeholder="Neues Mitglied suchen…" tid="ea-neu" />
            <MitgliedSuche wert={werber} onWahl={setWerber} placeholder="Werbendes Mitglied suchen…" ausser={neu?.id} tid="ea-werber" />
          </div>
          <Button size="sm" variant="outline" onClick={nachtragen} disabled={busy || !neu || !werber}>Empfehlung speichern</Button>
        </div>
      </CardContent>
    </Card>
  );
}
